import React, { useContext, useState, useEffect, useCallback } from "react";
import Avatar from "../../../assets/images/icon/placeholder_Awatar.png";
import { _imagesURL } from "../../../constants/API";
import ImageComponent from "../../common/ImageComponent";
import ProfileAvatar from "../../common/ProfileAvatar";
import {
  RiEditLine,
  RiImageAddLine,
  RiVideoAddLine,
  RiGroupLine,
} from "react-icons/ri";
import {
  MdOutlinePoll,
  MdOutlineSensors,
  MdOutlineMedicalServices,
} from "react-icons/md";
import { IoSparkles } from "react-icons/io5";
import { LuCalendarPlus } from "react-icons/lu";
import CreatePostPopup from "./CreatePostPopup";
import CreateAgendaEventModal from "../../agenda/CreateAgendaEventModal";
import AgendaInviteDrawer from "../../agenda/AgendaInviteDrawer";
import ProgressPopup from "../postTabs/ProgressPopup";
import InviteWebinarPopup from "../postTabs/InviteWebinarPopup";
import ClinicalSessionInvitePopup from "../postTabs/ClinicalSessionInvitePopup";
import usePostForm from "../../../hooks/home/usePostForm";
import { PostDetailContext, LoginUserData } from "../../../App";

/* ── colour tokens ─────────────────────────────────────── */
const C = {
  cyan: "#00BADA",
  cyanLight: "#E8FBFF",
  cyanBorder: "#BCEEFA",
  cyanGlow: "rgba(0, 186, 218, 0.15)",
  dark: "#0F1A2B",
  darkSoft: "#131722",
  textPrimary: "#0F1A2B",
  textSecondary: "#5F6673",
  textMuted: "#848798",
  border: "#D7E0EA",
  borderLight: "#F1F5F9",
  bgCard: "#FFFFFF",
  bgSurface: "#F8FAFC",
  bgInputHover: "#F0FAFF",
  orange: "#E65100",
  orangeLight: "#FFF3E0",
  amber: "#F59E0B",
  amberLight: "#FFFBEB",
  red: "#EF4444",
  redLight: "#FEF2F2",
  green: "#10B981",
  greenLight: "#ECFDF5",
  blue: "#3B82F6",
  blueLight: "#EFF6FF",
};

/* ── type-tab configs ──────────────────────────────────── */
export const TYPE_ICONS = {
  publish: { Icon: RiEditLine,              accent: C.cyan,   accentLight: C.cyanLight,  accentBorder: C.cyanBorder },
  poll:    { Icon: MdOutlinePoll,           accent: C.amber,  accentLight: C.amberLight, accentBorder: "#FDE68A" },
  webinar: { Icon: MdOutlineSensors,        accent: C.red,    accentLight: C.redLight,   accentBorder: "#FECACA" },
  study:   { Icon: MdOutlineMedicalServices,accent: C.green,  accentLight: C.greenLight, accentBorder: "#A7F3D0" },
  session: { Icon: RiGroupLine,             accent: C.blue,   accentLight: C.blueLight,  accentBorder: "#BFDBFE" },
  event:   { Icon: LuCalendarPlus,          accent: "#0474DF",accentLight: "#EBF5FF",    accentBorder: "#BFDBFE" },
};

export const resolveTypeKey = (label, type) => {
  const s = String(label || "").toLowerCase();
  if (s.includes("poll") || type === 2) return "poll";
  if (s.includes("webinar") || type === 3) return "webinar";
  if (s.includes("study") || s.includes("estudio") || type === 4) return "study";
  if (s.includes("session") || s.includes("sesión") || type === 5) return "session";
  if (s.includes("event") || s.includes("evento") || type === 6) return "event";
  return "publish";
};

/* ── reusable TypeTab ──────────────────────────────────── */
export const TypeTab = ({ label, typeKey, isActive, onClick }) => {
  const { Icon, accent, accentLight, accentBorder } = TYPE_ICONS[typeKey] || TYPE_ICONS.publish;

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: "0 0 auto",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        padding: "6px 13px",
        borderRadius: "999px",
        border: `1.5px solid ${isActive ? accentBorder : C.border}`,
        background: isActive ? accentLight : C.bgSurface,
        color: isActive ? accent : C.textSecondary,
        fontSize: "12.5px",
        fontWeight: isActive ? 700 : 500,
        fontFamily: "'Onest', sans-serif",
        cursor: "pointer",
        transition: "all 0.18s ease",
        boxShadow: isActive ? `0 2px 10px ${accent}22` : "none",
        outline: "none",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = accentLight;
          e.currentTarget.style.borderColor = accentBorder;
          e.currentTarget.style.color = accent;
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.background = C.bgSurface;
          e.currentTarget.style.borderColor = C.border;
          e.currentTarget.style.color = C.textSecondary;
        }
      }}
      aria-label={label}
    >
      <Icon
        size={14}
        style={{
          flexShrink: 0,
          color: isActive ? accent : C.textMuted,
          transition: "color 0.18s ease",
        }}
      />
      <span style={{ whiteSpace: "nowrap", lineHeight: 1.2 }}>
        {label}
      </span>
    </button>
  );
};

/* ── ActionChip for bottom bar ─────────────────────────── */
const ActionChip = ({ icon: IconEl, iconColor, label, bg, bgHover, textColor, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 14px",
      borderRadius: "999px",
      border: "none",
      background: bg,
      color: textColor,
      fontSize: "12.5px",
      fontWeight: 600,
      fontFamily: "'Onest', sans-serif",
      cursor: "pointer",
      transition: "all 0.18s ease",
      outline: "none",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = bgHover;
      e.currentTarget.style.transform = "translateY(-1px)";
      e.currentTarget.style.boxShadow = `0 3px 10px ${iconColor}30`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = bg;
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }}
    aria-label={label}
  >
    <IconEl size={15} color={iconColor} style={{ flexShrink: 0 }} />
    <span>{label}</span>
  </button>
);

/* ══════════════════════════════════════════════════════════
   PostForm Component
   ══════════════════════════════════════════════════════════ */
const PostForm = ({
  lang,
  splashData,
  selectedLabel,
  titleMessage,
  inviteWebinar,
  inviteClinical,
  showProgress,
  progress,
  webinarId,
  clinicalId,
  pages,
  setInviteWebinar,
  setInviteClinical,
  setProgress,
  handleApiCall,
  handleLabelClick,
  handleFormClick,
  handlePublishClick,
  selectedPost,
  editWebinar,
  setEditWebinar,
  editPost,
  setEditPost,
  showPost,
  setShowPost,
  refetch,
  handlePostSelect,
  updatePost,
}) => {
  const { isOpen } = useContext(PostDetailContext);
  const loginUserData = useContext(LoginUserData);
  const effectiveSplash = splashData || loginUserData?.splashData;
  const splashSaved = (() => {
    try {
      return JSON.parse(localStorage.getItem("SplashData"));
    } catch {
      return null;
    }
  })();
  const signupData = (() => {
    try {
      return JSON.parse(localStorage.getItem("SignupData"))?.data;
    } catch {
      return null;
    }
  })();
  const userImage =
    effectiveSplash?.user?.image ||
    splashSaved?.user?.image ||
    signupData?.image ||
    signupData?.user_image ||
    null;

  const [agendaModalOpen, setAgendaModalOpen] = useState(false);
  const [agendaInitialType, setAgendaInitialType] = useState("webinar");
  const [agendaEditEvent, setAgendaEditEvent] = useState(null);

  const [inviteDrawerOpen, setInviteDrawerOpen] = useState(false);
  const [inviteDrawerData, setInviteDrawerData] = useState({ itemId: null, type: "webinar" });
  const [isInviteDrawerMounted, setIsInviteDrawerMounted] = useState(false);

  const handleOpenInviteDrawer = useCallback(({ itemId, type }) => {
    if (!itemId) return;
    setInviteDrawerData({ itemId, type: type || "webinar" });
    setIsInviteDrawerMounted(true);
    setInviteDrawerOpen(true);
  }, []);

  const handleCloseInviteDrawer = useCallback(() => {
    setInviteDrawerOpen(false);
  }, []);

  useEffect(() => {
    if (inviteWebinar && webinarId) {
      handleOpenInviteDrawer({ itemId: webinarId, type: "webinar" });
      setInviteWebinar(false);
    }
  }, [inviteWebinar, webinarId, handleOpenInviteDrawer, setInviteWebinar]);

  useEffect(() => {
    if (inviteClinical && clinicalId) {
      handleOpenInviteDrawer({ itemId: clinicalId, type: "clinical_session" });
      setInviteClinical(false);
    }
  }, [inviteClinical, clinicalId, handleOpenInviteDrawer, setInviteClinical]);

  useEffect(() => {
    if (editWebinar && selectedPost) {
      setAgendaInitialType("webinar");
      setAgendaEditEvent(selectedPost);
      setAgendaModalOpen(true);
    }
  }, [editWebinar, selectedPost]);

  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState(null);

  const handleCardDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleCardDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleCardDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingOver(false);
    }
  };

  const handleCardDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) {
      setDroppedFiles(files);
      handlePublishClick();
      handleFormClick();
      if (setShowPost) setShowPost(true);
    }
  };

  const isPublishActive = selectedLabel === (lang?.publish || "Publish");

  return (
    <>
      {/* ─── Card Container ─── */}
      <div
        className={`mb-3 ${isOpen && (editWebinar || editPost) ? "d-none" : ""}`}
        style={{
          position: "relative",
          overflow: "hidden",
          border: isDraggingOver ? `2px dashed ${C.cyan}` : `1px solid ${C.border}`,
          borderRadius: "24px",
          background: isDraggingOver
            ? `linear-gradient(180deg, #E8FBFF 0%, #F0FDFF 100%)`
            : `linear-gradient(180deg, ${C.bgCard} 0%, #F8FDFF 100%)`,
          padding: "16px 16px 14px",
          width: "100%",
          maxWidth: "100%",
          boxShadow: isDraggingOver
            ? `0 0 0 4px ${C.cyanGlow}, 0 12px 35px rgba(0, 186, 218, 0.15)`
            : "0 8px 30px rgba(0, 186, 218, 0.06), 0 2px 8px rgba(0, 0, 0, 0.03)",
          fontFamily: "'Onest', sans-serif",
          cursor: "pointer",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          transform: isDraggingOver ? "scale(1.01)" : "none",
        }}
        onClick={() => {
          handlePublishClick();
          handleFormClick();
        }}
        onDragOver={handleCardDragOver}
        onDragEnter={handleCardDragEnter}
        onDragLeave={handleCardDragLeave}
        onDrop={handleCardDrop}
      >
        {/* Drag and Drop Active Overlay */}
        {isDraggingOver && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 10,
              background: "rgba(232, 251, 255, 0.94)",
              backdropFilter: "blur(4px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              pointerEvents: "none",
              borderRadius: "24px",
              border: `2px dashed ${C.cyan}`,
              animation: "fadeIn 0.15s ease-out",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                background: "#FFFFFF",
                boxShadow: "0 4px 14px rgba(0, 186, 218, 0.2)",
                display: "grid",
                placeItems: "center",
                color: C.cyan,
              }}
            >
              <RiImageAddLine size={24} />
            </div>
            <div
              style={{
                fontWeight: 700,
                fontSize: "14px",
                color: C.dark,
              }}
            >
              {lang?.drop_media_to_post || "Drop photos or videos here"}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: C.textSecondary,
                fontWeight: 500,
              }}
            >
              {lang?.drop_media_sub || "Release to automatically start your post"}
            </div>
          </div>
        )}
        {/* Ambient Glow */}
        <div
          style={{
            position: "absolute",
            top: "-30px",
            right: "-30px",
            width: "120px",
            height: "120px",
            background: C.cyan,
            opacity: 0.09,
            filter: "blur(45px)",
            borderRadius: "50%",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* ─── Type Tabs Row ─── */}
        <div
          className="d-flex align-items-center flex-nowrap overflow-x-auto"
          style={{
            position: "relative",
            zIndex: 1,
            gap: "6px",
            paddingBottom: "11px",
            marginBottom: "12px",
            borderBottom: `1px solid ${C.borderLight}`,
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            width: "100%",
          }}
        >
          {/* Publish Tab */}
          <TypeTab
            label={lang?.publish || "Publish"}
            typeKey="publish"
            isActive={isPublishActive}
            onClick={(e) => {
              e.stopPropagation();
              handlePublishClick();
              handleFormClick();
            }}
          />

          {/* Dynamic Post Type Tabs */}
          {pages.map((val, i) => {
            const typeKey = resolveTypeKey(val.label, val.type);
            return (
              <TypeTab
                key={i}
                label={val.label}
                typeKey={typeKey}
                isActive={selectedLabel === val.label}
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeKey === "webinar") {
                    setAgendaInitialType("webinar");
                    setAgendaEditEvent(null);
                    setAgendaModalOpen(true);
                  } else if (typeKey === "session") {
                    setAgendaInitialType("clinical_session");
                    setAgendaEditEvent(null);
                    setAgendaModalOpen(true);
                  } else if (typeKey === "event") {
                    setAgendaInitialType("event");
                    setAgendaEditEvent(null);
                    setAgendaModalOpen(true);
                  } else {
                    handleLabelClick(val.label);
                    handleFormClick();
                  }
                }}
              />
            );
          })}
        </div>

        {/* ─── Input Trigger Row ─── */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "14px",
            cursor: "pointer",
          }}
          onClick={handleFormClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && handleFormClick()}
          aria-label="Open post form"
        >
          {/* Reusable ProfileAvatar with gradient ring */}
          <ProfileAvatar
            src={userImage}
            size={44}
            variant="gradient"
            alt={lang?.profile || "Profile"}
          />

          {/* Prompt Capsule */}
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "11px 16px",
              borderRadius: "999px",
              background: C.bgSurface,
              border: `1.5px solid ${C.borderLight}`,
              color: C.textMuted,
              fontSize: "13.5px",
              fontWeight: 400,
              minHeight: "44px",
              transition: "all 0.2s ease",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = C.bgInputHover;
              e.currentTarget.style.borderColor = C.cyanBorder;
              e.currentTarget.style.boxShadow = `0 0 0 3px ${C.cyanGlow}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = C.bgSurface;
              e.currentTarget.style.borderColor = C.borderLight;
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <IoSparkles
              size={15}
              style={{ flexShrink: 0, color: C.cyan, opacity: 0.75 }}
            />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              {lang?.post_form_placeholder || "What's on your mind? Share clinical insights..."}
            </span>
          </div>
        </div>

        {/* ─── Bottom Actions Bar ─── */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            paddingTop: "12px",
            borderTop: `1px solid ${C.borderLight}`,
            gap: "8px",
          }}
        >
          <ActionChip
            icon={RiImageAddLine}
            iconColor={C.cyan}
            label={lang?.images || "Images"}
            bg={C.cyanLight}
            bgHover="#D4F6FD"
            textColor={C.cyan}
            onClick={(e) => {
              e.stopPropagation();
              handlePublishClick();
              handleFormClick();
            }}
          />
          <ActionChip
            icon={RiVideoAddLine}
            iconColor={C.orange}
            label="Video"
            bg={C.orangeLight}
            bgHover="#FFE0B2"
            textColor={C.orange}
            onClick={(e) => {
              e.stopPropagation();
              handlePublishClick();
              handleFormClick();
            }}
          />
        </div>
      </div>

      {/* ─── Create Post Drawer (Right Side) ─── */}
      {showPost && !editWebinar && (
        <CreatePostPopup
          selectedLabel={selectedLabel}
          refetch={refetch}
          selectedPost={selectedPost}
          editWebinar={editWebinar}
          setEditWebinar={setEditWebinar}
          editPost={editPost}
          setEditPost={setEditPost}
          show={showPost}
          initialDroppedFiles={droppedFiles}
          onHide={() => {
            setShowPost(false);
            setDroppedFiles(null);
            if (setEditWebinar) setEditWebinar(false);
            if (setEditPost) setEditPost(false);
          }}
          setProgress={setProgress}
          handleApiCall={handleApiCall}
          handlePostSelect={handlePostSelect}
          updatePost={updatePost}
        />
      )}

      {/* ─── Create Agenda Event / Webinar / Clinical Session Drawer ─── */}
      {agendaModalOpen && (
        <CreateAgendaEventModal
          isOpen={agendaModalOpen}
          toggle={() => {
            setAgendaModalOpen(false);
            setAgendaEditEvent(null);
            if (setEditWebinar) setEditWebinar(false);
            if (setShowPost) setShowPost(false);
          }}
          initialType={agendaInitialType}
          allowedTypes={["event", "webinar", "clinical_session"]}
          hideTopCards={false}
          hideMentorshipNotice={true}
          editEvent={agendaEditEvent}
          onAddEvent={() => {
            if (refetch) refetch();
          }}
          onUpdateEvent={() => {
            if (refetch) refetch();
          }}
          onInviteSchedule={handleOpenInviteDrawer}
        />
      )}

      {/* ─── Agenda Invite Drawer for Webinar & Clinical Session ─── */}
      {isInviteDrawerMounted && (
        <AgendaInviteDrawer
          isOpen={inviteDrawerOpen}
          toggle={handleCloseInviteDrawer}
          itemId={inviteDrawerData.itemId}
          type={inviteDrawerData.type}
          onDone={() => {
            handleCloseInviteDrawer();
            if (refetch) refetch();
          }}
        />
      )}

      {showProgress && (
        <ProgressPopup
          title={titleMessage}
          message={lang.uploading_in_progress}
          progress={progress}
          isVisible={showProgress}
        />
      )}
      {inviteWebinar && (
        <InviteWebinarPopup
          isOpen={inviteWebinar}
          webinarId={webinarId}
          toggleModal={() => setInviteWebinar(!inviteWebinar)}
        />
      )}
      {inviteClinical && (
        <ClinicalSessionInvitePopup
          isOpen={inviteClinical}
          toggleModal={() => setInviteClinical(!inviteClinical)}
          clinicalId={clinicalId}
        />
      )}
    </>
  );
};

const MemoizedPostForm = React.memo(PostForm);

// Wrapper Component
const PostFormWrapper = (props) => {
  const postFormProps = usePostForm(props);
  return <MemoizedPostForm {...postFormProps} />;
};

export default PostFormWrapper;
